import { App, Plugin, watch } from 'vue';
import type { Router } from 'vue-router';
import axios from 'axios';

import { useAppSidebarMenuStore } from '@lib/stores/app-sidebar-menu';
import { useAppContextStore } from '@lib/plugins/layouts/context';

import type { SidebarItemType, SidebarItemRouterType, SidebarItemLinkType } from '@lib/stores/app-sidebar-menu';
import type { AuthUser } from '@lib/types';

type SidebarSection = 'user' | 'staff' | 'hr' | 'admin';

type SidebarEntry = {
	section: SidebarSection;
	item: SidebarItemRouterType | SidebarItemLinkType;
	permission?: string;
	condition?: (user: AuthUser | null) => boolean;
};

const sectionOrder: SidebarSection[] = ['user', 'staff', 'hr', 'admin'];

const sectionHeaders: Partial<Record<SidebarSection, SidebarItemType[]>> = {
	staff: [{ divider: true }, { label: 'staff_header' }],
	hr: [{ divider: true }, { label: 'hr_header' }],
};

// Sections rendered as a collapsible group instead of a header with flat items
const sectionGroups: Partial<Record<SidebarSection, { label: string; icon: string }>> = {
	admin: { label: 'admin_header', icon: 'fa fa-user-shield' },
};

const registry: SidebarEntry[] = [];

function addSidebarItem(entry: SidebarEntry): void {
	registry.push(entry);
}

// ---- User ----
addSidebarItem({ section: 'user', item: { to: '/dashboard', icon: 'fa-home', label: 'user_dashboard' } });
addSidebarItem({ section: 'user', item: { to: '/events', icon: 'fa fa-campground', label: 'user_events' } });
addSidebarItem({ section: 'user', item: { to: '/calendar', icon: 'fa fa-calendar', label: 'user_calendar' } });
addSidebarItem({ section: 'user', item: { to: '/profile', icon: 'fa fa-user', label: 'user_profile' } });
addSidebarItem({ section: 'user', item: { to: '/documents', icon: 'fa fa-file', label: 'user_documents' } });
addSidebarItem({
	section: 'user',
	item: { to: '/time_tracking', icon: 'fa fa-business-time', label: 'user_timetracking' },
});
addSidebarItem({ section: 'user', item: { to: '/mails', icon: 'fa fa-envelope', label: 'user_mails' } });

// ---- Staff ----
addSidebarItem({
	section: 'staff',
	item: { to: '/staff/staffing_requests', icon: 'fa fa-user-group', label: 'staff_staffing_requests' },
	permission: 'staffing_requests.create',
});
addSidebarItem({
	section: 'staff',
	item: { to: '/staff/crews', icon: 'fa fa-user-friends', label: 'staff_crews' },
	condition: (user) => !!user?.attributes?.isCrewLead,
});
addSidebarItem({
	section: 'staff',
	item: { to: '/staff/time_tracking', icon: 'fa fa-business-time', label: 'staff_timetracking' },
	condition: (user) => !!user?.attributes?.isCrewLead,
});

// ---- HR ----
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/users', icon: 'fa fa-users', label: 'users' },
	permission: 'users.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/applications', icon: 'fa fa-address-card', label: 'hr_applications' },
	permission: 'applications.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/staffing_requests', icon: 'fa fa-user-group', label: 'hr_staffing_requests' },
	permission: 'staffing_requests.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/subscriptions', icon: 'fa fa-bell', label: 'hr_subscriptions' },
	permission: 'subscriptions.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/events', icon: 'fa fa-campground', label: 'hr_events' },
	permission: 'events.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/planning', icon: 'fa fa-clock', label: 'hr_schedule_employees' },
	permission: 'plannings.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/contracts', icon: 'fa fa-file-signature', label: 'hr_contracts' },
	permission: 'plannings.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/payroll', icon: 'fa fa-money-bill-1', label: 'hr_payroll' },
	permission: 'payroll.read',
});
addSidebarItem({
	section: 'hr',
	item: { to: '/hr/mail', icon: 'fa fa-envelope', label: 'hr_mails' },
	permission: 'mails.read',
});

// ---- Admin ----
addSidebarItem({
	section: 'admin',
	item: { to: '/admin/users', icon: 'fa fa-users', label: 'admin_users' },
	permission: 'users.read',
});
addSidebarItem({
	section: 'admin',
	item: { to: '/admin/settings', icon: 'fa fa-cogs', label: 'admin_settings' },
	permission: 'app.settings',
});
addSidebarItem({
	section: 'admin',
	item: { to: '/admin/maintenance', icon: 'fa fa-screwdriver-wrench', label: 'maintenance' },
	permission: 'app.developer',
});
addSidebarItem({
	section: 'admin',
	// opener: the Swagger UI page reads the SPA session from the tab that opened it
	item: { href: '/docs/swagger', icon: 'fa fa-book', label: 'swagger', opener: true },
	permission: 'app.developer',
});

// ---- Builder ----
function buildSidebar(auth: {
	check: (permission: string) => boolean;
	user: () => AuthUser | null;
}): SidebarItemType[] {
	const groups = new Map<SidebarSection, Array<SidebarItemRouterType | SidebarItemLinkType>>();
	const user = auth.user();

	for (const { section, item, permission, condition } of registry) {
		if (permission && !auth.check(permission)) continue;
		if (condition && !condition(user)) continue;
		const group = groups.get(section) ?? [];
		group.push(item);
		groups.set(section, group);
	}

	const result: SidebarItemType[] = [];
	for (const section of sectionOrder) {
		const items = groups.get(section);
		if (!items?.length) continue;
		const group = sectionGroups[section];
		if (group) {
			result.push({ divider: true }, { ...group, children: items });
		} else {
			result.push(...(sectionHeaders[section] ?? []), ...items);
		}
	}

	return result;
}

export default {
	install: (app: App) => {
		// We are not in a vue instance yet, injection based useComposables do not work, pinia stores do
		const auth = app.config.globalProperties.$auth;
		const appContextStore = useAppContextStore();

		watch([() => auth.user(), () => appContextStore.getCurrentContext], (user, context) => {
			if (user && context) {
				const router: Router | undefined = app.config.globalProperties.$router;
				const appContext = useAppContextStore();
				const sidebarMenuStore = useAppSidebarMenuStore();

				sidebarMenuStore.set(buildSidebar(auth));

				let fetching = false;
				async function fetchAndApplyBadges() {
					if (fetching) return;

					fetching = true;
					try {
						const { data: badges } = await axios.get(`/me/sidebar_badges`);
						const applyBadges = (items: Array<SidebarItemType>) => {
							items.forEach((item) => {
								if ('children' in item) {
									applyBadges(item.children);
								} else if ('to' in item && item.to && 'label' in item && item.label) {
									const v = badges[item.label];
									item.badge_text = v > 0 ? v : undefined;
								}
							});
						};
						applyBadges(sidebarMenuStore.$state.items);
					} finally {
						fetching = false;
					}
				}

				watch(
					() => [auth.user(), appContext.getContextPath],
					([newUser, newContextPath]) => {
						if (newUser && newContextPath) fetchAndApplyBadges();
					},
				);

				if (router) {
					router.afterEach(() => {
						if (auth.user()) {
							fetchAndApplyBadges();
						}
					});
				}
			}
		});
	},
} as Plugin;
